import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useObserver } from 'mobx-react-lite';
import useInstoreCart from 'hooks/use-instore-cart';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { editBankAccount, relinkBankAccount } from 'src/payments/constants';
import {
  useDutchiePayAnalytics,
  INSTORE_EVENTS,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { Tip } from 'src/components/tip/tip';
import { useDutchiePay } from 'src/payments/hooks';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { useIsFirstRender } from 'src/hooks/use-is-first-render';
import { UNLINKED_ACCOUNT_ERROR } from 'src/payments/hooks/use-payments-error/use-payments-error';
import { ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';
import {
  ConfettiOverlay,
  InstoreCartItems,
  PaymentInformation,
  PayNowButton,
  PaymentCompleteHeader,
  PaymentUnsuccessfulHeader,
  Totals,
} from '../../components';

const InstoreCartPage = ({ onCartApprove, paymentComplete, paymentLoading, paymentUnsuccessful }): JSX.Element => {
  const showErnie = useErnie();
  const InstoreCart = useInstoreCart();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const { openDutchiePay } = useOpenDutchiePay({
    onCloseModal: () => DutchiePayEnrollment.setAutoCheckoutAfterBankLinkReconnect(false),
  });
  const isFirstRender = useIsFirstRender();
  const { tipData, id, tippingDisabled } = InstoreCart;
  const totalPrice = useObserver(() => InstoreCart.totalPrice);
  const dispensary = useObserver(() => InstoreCart.dispensary);

  const dispensaryName = dispensary.name ?? '';
  const dispensaryId = dispensary.id ?? '';

  const { bankAccounts, needsRelinkBankAccount } = useDutchiePay();
  const { dutchiePayEventTracker } = useDutchiePayAnalytics();

  const bankInformation = {
    bankName: bankAccounts[0]?.bankName,
    lastDigits: bankAccounts[0]?.lastDigits,
  };

  const setTip = (amount): void => {
    InstoreCart.tip = amount;
  };

  const ConditionalSection = ({ renderSection, children }): JSX.Element =>
    renderSection ? <Section>{children}</Section> : children;

  const connectAndOpenDutchiePay = (step = ''): void => {
    DutchiePayEnrollment.reconnectBankAccount();
    openDutchiePay(step);
  };

  useEffect(() => {
    if (dispensaryId) {
      const payload = {
        dispensary: {
          id: dispensaryId,
          name: dispensaryName,
        },
        instoreCartId: id,
      };
      dutchiePayEventTracker(INSTORE_EVENTS.CART_VIEWED, payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispensaryId, dispensaryName, id]);

  useEffect(() => {
    if (isFirstRender && needsRelinkBankAccount) {
      showErnie(UNLINKED_ACCOUNT_ERROR, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
    }
  }, [isFirstRender, needsRelinkBankAccount, showErnie]);

  return (
    <CartContainer data-testid='instore-cart'>
      {paymentComplete && <ConfettiOverlay />}

      {paymentComplete && (
        <>
          <PaymentCompleteHeader />
          <Spacer />
        </>
      )}

      {paymentUnsuccessful && <PaymentUnsuccessfulHeader />}

      {!paymentUnsuccessful && (
        <>
          <ConditionalSection renderSection={!paymentComplete}>
            <InstoreCartItems />
          </ConditionalSection>

          {!tippingDisabled && (
            <Section>
              <Tip tipData={tipData} onChangeTipAmount={setTip} readonly={paymentComplete} />
            </Section>
          )}

          <Section>
            <Totals paymentComplete={paymentComplete} />
          </Section>

          <Section>
            <PaymentInformation
              bankInformation={{ ...bankInformation, bankLogo: bankAccounts[0]?.financialInstitutionLogo }}
              needsRelinkBankAccount={needsRelinkBankAccount}
              relinkBankAccount={() => connectAndOpenDutchiePay(relinkBankAccount)}
              editBankAccount={() => connectAndOpenDutchiePay(editBankAccount)}
              paymentComplete={paymentComplete}
            />
          </Section>

          {!paymentComplete && (
            <PayNowButton
              disabled={needsRelinkBankAccount || !id}
              loading={paymentLoading}
              onClick={onCartApprove}
              total={totalPrice}
            />
          )}
        </>
      )}
    </CartContainer>
  );
};
export default InstoreCartPage;

const Spacer = styled.div`
  margin-bottom: 47px;
`;

export const Section = styled.section<{ showBorder?: boolean }>`
  ${({ theme: { colors, spaces }, showBorder = true }) => css`
    margin-bottom: ${spaces[4]};
    padding-top: ${spaces[4]};
    width: 100%;

    ${showBorder &&
    css`
      border-top: 1px solid ${colors.grey[90]};
    `}
  `}
`;

const CartContainer = styled.div`
  width: 100%;
`;
